import React, { useMemo } from 'react';
import { Form, Formik, type FormikProps } from 'formik';
import { PageHeader } from '../../../constants/style/common.styled';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import FormSelect from '../../../components/dropdown/formSelect';
import Button from '../../../components/button';
import InitialValuesForm from '../../../constants/initialValues';
import useGetCompanies from '../../../hooks/get/useGetCompanies';
import useGetVendorSelfRolesByCompany from '../../../hooks/get/useGetVendorSelfRolesByCompany';
import usePostCustomerSuccessMappingBundle from '../../../hooks/post/usePostCustomerSuccessMappingBundle';
import { useWealthlaneCustomerApi, useWealthlaneIdentityApi } from '../../../hooks';
import { Wealthlane_Dtos_CustomerSuccess_UpsertCustomerSuccessMappingBundleDto } from '../../../services/wealthlane-identity-services/models/Wealthlane_Dtos_CustomerSuccess_UpsertCustomerSuccessMappingBundleDto';
import { CUSTOMER_SUCCESS_AUTOMATION_VALIDATION } from '../../../utils/validations/validations';
type CustomerSuccessAutomationValues = {
  sourceCompanyId: string | null;
  destinationCompanyId: string | null;
  sourceRoleId: string | null;
  destinationRoleId: string | null;
};

type CustomerSuccessAutomationFormFieldsProps = FormikProps<CustomerSuccessAutomationValues> & {
  isSaving: boolean;
};

const CustomerSuccessAutomationFormFields = ({
  touched,
  errors,
  setFieldValue,
  values,
  isSaving,
}: CustomerSuccessAutomationFormFieldsProps): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: companies, isLoading: isCompaniesLoading } = useGetCompanies(handleRequest);
  const { data: rolePublishOptions, isLoading: isRolePublishLoading } =
    useGetVendorSelfRolesByCompany(handleRequest, values.sourceCompanyId ?? '');
  const { data: roleMappingOptions, isLoading: isRoleMappingLoading } =
    useGetVendorSelfRolesByCompany(handleRequest, values.destinationCompanyId ?? '');

  const filteredSourceCompanies = useMemo(
    () => companies?.filter((company) => company.value !== values.destinationCompanyId) ?? [],
    [companies, values.destinationCompanyId]
  );

  const filteredDestinationCompanies = useMemo(
    () => companies?.filter((company) => company.value !== values.sourceCompanyId) ?? [],
    [companies, values.sourceCompanyId]
  );

  const handleSourceCompanyChange = (selectedSourceCompanyId: string): void => {
    setFieldValue('sourceCompanyId', selectedSourceCompanyId ?? null);
    setFieldValue('sourceRoleId', null);
  };

  const handleDestinationCompanyChange = (selectedDestinationCompanyId: string): void => {
    setFieldValue('destinationCompanyId', selectedDestinationCompanyId ?? null);
    setFieldValue('destinationRoleId', null);
  };

  const isSaveDisabled =
    !values.sourceCompanyId ||
    !values.destinationCompanyId ||
    !values.sourceRoleId ||
    !values.destinationRoleId;

  return (
    <Form id='customerSuccessAutomationForm'>
      <div className='rounded-lg border border-gray-200 bg-white p-6' id='automationFormCard'>
        <Heading variant='body' title='Companies' className='mb-4' />
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <FormSelect
            label='Source Company'
            name='sourceCompanyId'
            id='sourceCompanyId'
            containerClassName='formGroup'
            onChange={handleSourceCompanyChange}
            errors={errors}
            touched={touched}
            errorValue={errors?.sourceCompanyId}
            options={filteredSourceCompanies}
            arrValue={values?.sourceCompanyId}
            setFieldValue={setFieldValue}
            isLoading={isCompaniesLoading}
            isClearable
            required
            placeholder='Select source company'
          />
          <FormSelect
            label='Destination Company'
            name='destinationCompanyId'
            id='destinationCompanyId'
            containerClassName='formGroup'
            onChange={handleDestinationCompanyChange}
            errors={errors}
            touched={touched}
            errorValue={errors?.destinationCompanyId}
            options={filteredDestinationCompanies}
            arrValue={values?.destinationCompanyId}
            setFieldValue={setFieldValue}
            isLoading={isCompaniesLoading}
            isClearable
            required
            placeholder='Select destination company'
          />
        </div>

        <Heading variant='body' title='Role configuration' className='mb-4 mt-6' />
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <FormSelect
            label='Role Publish'
            name='sourceRoleId'
            id='sourceRoleId'
            containerClassName='formGroup'
            onChange={(value: string) => setFieldValue('sourceRoleId', value ?? null)}
            errors={errors}
            touched={touched}
            errorValue={errors?.sourceRoleId}
            options={rolePublishOptions ?? []}
            arrValue={values?.sourceRoleId}
            setFieldValue={setFieldValue}
            isLoading={isRolePublishLoading}
            disabled={!values.sourceCompanyId}
            isClearable
            required
            placeholder='Select role publish'
          />
          <FormSelect
            label='Role Mapping'
            name='destinationRoleId'
            id='destinationRoleId'
            containerClassName='formGroup'
            onChange={(value: string) => setFieldValue('destinationRoleId', value ?? null)}
            errors={errors}
            touched={touched}
            errorValue={errors?.destinationRoleId}
            options={roleMappingOptions ?? []}
            arrValue={values?.destinationRoleId}
            setFieldValue={setFieldValue}
            isLoading={isRoleMappingLoading}
            disabled={!values.destinationCompanyId}
            isClearable
            required
            placeholder='Select role mapping'
          />
        </div>

        <div className='mt-8 flex justify-end' id='formActions'>
          <Button
            title='Save'
            type='submit'
            variant='primary'
            id='saveButton'
            loading={isSaving}
            disabled={isSaveDisabled || isSaving}
          />
        </div>
      </div>
    </Form>
  );
};

const CustomerSuccessAutomation = (): JSX.Element => {
  const { handleRequest: identityHandleRequest } = useWealthlaneIdentityApi();

  const { mutate: saveMappingBundle, isLoading: isSaving } =
    usePostCustomerSuccessMappingBundle(identityHandleRequest);

  return (
    <div data-page='customerSuccessAutomation' id='pageContainer'>
      <PageHeader id='pageHeader'>
        <Heading variant='h3' title='Customer Success Automation' id='pageTitle' />
        <Heading
          variant='body-s'
          title='Configure role publish and role mapping between source and destination companies.'
          color={COLORS.Gray}
          id='pageDescription'
        />
      </PageHeader>

      <Formik<CustomerSuccessAutomationValues>
        enableReinitialize
        initialValues={InitialValuesForm.customerSuccessAutomation}
        validationSchema={CUSTOMER_SUCCESS_AUTOMATION_VALIDATION}
        onSubmit={(values, { resetForm }) => {
          const payload: Wealthlane_Dtos_CustomerSuccess_UpsertCustomerSuccessMappingBundleDto = {
            sourceCompanyId: values.sourceCompanyId!,
            destinationCompanyId: values.destinationCompanyId!,
            sourceRoleId: values.sourceRoleId!,
            destinationRoleId: values.destinationRoleId!,
          };

          saveMappingBundle(payload, {
            onSuccess: () => {
              resetForm();
            },
          });
        }}>
        {(formikProps) => (
          <CustomerSuccessAutomationFormFields {...formikProps} isSaving={isSaving} />
        )}
      </Formik>
    </div>
  );
};

export default CustomerSuccessAutomation;
